import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { apiCore, setAuthorization } from "../api/apiCore";
import { API_USER_AUTH, API_USER_LOGIN } from "../api/urls";
import { IUser } from "../types/user";

interface AuthContextType {
  user: IUser | null;
  loading: boolean;
  login: (data: any) => Promise<IUser>;
  logout: () => void;
  updateUser: (data: any) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);
let logoutCallback: (() => void) | null = null;

export const registerLogout = (fn: () => void) => {
  logoutCallback = fn;
};

export const forceLogout = () => {
  if (logoutCallback) {
    logoutCallback();
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);

  //Auth, quando abrir app ja logado
  useEffect(() => {
    const bootstrapAuth = async () => {
      try {
        const raw = localStorage.getItem("authUser");
        if (!raw) return;

        const stored = JSON.parse(raw);

        if (!stored.tokenJWT) {
          logout();
          return;
        }

        // 🔐 seta token no axios
        setAuthorization(stored.tokenJWT);
        const response = await apiCore<IUser>(API_USER_AUTH, {
          method: "POST",
          // body: data,
        });

        // 🔒 Remove password antes de persistir
        const { password, ...safeUser } = response.data as any;

        // 🔐 seta usuario: autenticado
        setUser(safeUser);
        localStorage.setItem("authUser", JSON.stringify(safeUser));


      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  // 🔹 REGISTRA LOGOUT GLOBAL (ESSE É O TRECHO NOVO 👇)
  useEffect(() => {
    registerLogout(logout);
  }, []);

  const isValidUser = (user: any): user is IUser => {
    if (!user) return false;

    if (!user.id || user.id <= 0) return false;

    if (!user.isActive || user.isActive == false) return false;

    if (!user.email) return false;

    return true;
  };

  const login = async (data: any): Promise<IUser> => {
    try {
      setLoading(true);

      const response = await apiCore<IUser>(API_USER_LOGIN, {
        method: "POST",
        body: data,
      });

      if (response.status === 204 || !response.data) {
        throw "Usuário ou senha inválidos";
      }

      if (!isValidUser(response.data)) {
        throw "Usuário ou senha inválidos";
      }

      // 🔒 Remove password antes de persistir
      const { password, ...safeUser } = response.data as any;

      // 🔐 seta token no axios
      setAuthorization(response.data.tokenJWT);

      // 🔐 seta usuario: autenticado
      setUser(safeUser);
      localStorage.setItem("authUser", JSON.stringify(safeUser));

      return safeUser;

    } catch (err) {
      // console.error("[AuthContext] login error", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {

    console.log("🔴 LOGOUT → Disconnecting SignalR...");

    setUser(null);        // ⛔ DEPOIS mata o usuário
    setAuthorization(null);
    localStorage.removeItem("authUser");

  };

  // const logout = () => {
  //   setUser(null);
  //   setAuthorization(null);
  //   localStorage.removeItem("authUser");
  // };

  const updateUser = (data: any) => {
    localStorage.removeItem("authUser");
    setUser(data);
    localStorage.setItem("authUser", JSON.stringify(data));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
};
